import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Button,
  Input,
  Card,
  Chip,
} from '../../components';
import { useInspectionContext } from '../../hooks';
import { validatePlate } from '../../utils';

export const PlateConfirmPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, setPlate, confirmPlate } = useInspectionContext();

  const inputRef = useRef<HTMLInputElement>(null);
  const [validation, setValidation] = useState<{ valid: boolean; error?: string }>({ valid: true });

  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Validate on change
  const handlePlateChange = (value: string) => {
    const upper = value.toUpperCase();
    setPlate(upper);
    
    // Validate as user types (debounced feel)
    const result = validatePlate(upper);
    setValidation({ valid: result.valid, error: result.error });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = validatePlate(state.plate);
    
    if (result.valid && result.formatted) {
      setPlate(result.formatted);
      confirmPlate();
      navigate('/inspecao/captura');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && validation.valid) {
      handleSubmit(e);
    }
  };

  return (
    <Page
      appBar={{
        title: 'Nova Vistoria',
        leading: <span className="material-symbols-outlined">arrow_back</span>,
        leadingAction: () => navigate('/'),
      }}
      className="pb-24"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Progress indicator */}
        <div className="flex items-center gap-2" role="progressbar" aria-valuenow={1} aria-valuemin={1} aria-valuemax={4}>
          <div className="flex-1 h-1 bg-primary rounded-full" />
          <div className="flex-1 h-1 bg-surface-container-high rounded-full" />
          <div className="flex-1 h-1 bg-surface-container-high rounded-full" />
          <div className="flex-1 h-1 bg-surface-container-high rounded-full" />
        </div>

        <div className="text-center mb-4">
          <h2 className="font-headline-lg">Placa do Veículo</h2>
          <p className="font-body-md text-on-surface-variant mt-1">
            Digite a placa para iniciar a vistoria guiada
          </p>
        </div>

        {/* Plate Input */}
        <Card variant="elevated" padding="lg">
          <Input
            ref={inputRef}
            label="Placa"
            placeholder="ABC1D23"
            value={state.plate}
            onChange={(e) => handlePlateChange(e.target.value)}
            onKeyDown={handleKeyDown}
            error={validation.error}
            helperText={validation.valid ? 'Placa válida' : undefined}
            autoComplete="off"
            autoCapitalize="characters"
            maxLength={7}
            inputMode="text"
          />

          {/* Plate format hint */}
          <div className="mt-4 p-3 rounded-lg bg-surface-container-low">
            <p className="font-label-sm text-on-surface-variant mb-2">Formatos aceitos:</p>
            <div className="flex flex-wrap gap-2">
              <Chip variant="default" size="sm">
                Mercosul: ABC1D23
              </Chip>
              <Chip variant="default" size="sm">
                Antiga: ABC-1234
              </Chip>
            </div>
          </div>
        </Card>

        {/* Quick actions for common plates (demo) */}
        {import.meta.env.DEV && (
          <Card variant="outlined" padding="md">
            <p className="font-label-sm text-on-surface-variant mb-3">Placas de teste (dev):</p>
            <div className="flex flex-wrap gap-2">
              {['ABC1D23', 'XYZ9K88', 'FOO2B11', 'BAR3C22'].map((plate) => (
                <Button
                  key={plate}
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => {
                    setPlate(plate);
                    confirmPlate();
                    navigate('/inspecao/captura');
                  }}
                >
                  {plate}
                </Button>
              ))}
            </div>
          </Card>
        )}

        {/* Continue Button */}
        <Button
          variant="primary"
          fullWidth
          type="submit"
          disabled={!validation.valid}
          className="mt-auto"
        >
          Continuar para Captura
        </Button>
      </form>
    </Page>
  );
};